import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";
import css from './Header.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';


/**
 * @class
 *
 * The common header component found throughout the app.
 *
 * The header links are automatically generated based on the class variable, `headerLinks`. To add a new header link,
 * add an new element to `headerLinks` -- this should be an object with the following properties:
 *      label {string}: Displayed label/text of the header link.
 *      route {string}: The client-side route for the link.
 * When adding/updating the `route`, you'll also need to add/update a <Route> inside `Home.js` to catch the route. See the `Home.js`
 * for examples on doing this.
 *
 * The mobile navigation links are automatically generated from the same `headerLinks` list, so there's no additional work needed for mobile view.
 * The mobile view is triggered based on viewport width.
 *
 * Aside: there's a special condition for the `/logout` route so that it renders the logout icon (without any text).
 */
const Header = class extends Component {
    // ---------- state: ----------
    state = {
        isMobileNavActive: false,
        headerLinks: []
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        let headerLinks;

        // show header tabs based on user role:
        switch (this.props.user.role) {
            // admin roles:
            case 'office':
                headerLinks = [
                    {label: 'Scheduled Jobs', route: '/scheduled_jobs'},
                    {label: 'Work Orders', route: '/workOrders'},
                    {label: 'Field Customers', route: '/fieldCustomers'},
                    {label: 'HMOD Scoreboard', route: '/hmodScoreboard'},
                    {label: 'Field Kpis', route: '/fieldKpis'},
                    {label: 'Leads', route: '/leads'},
                    {label: 'Customers', route: '/customers'},
                    {label: 'KPIS', route: '/kpis'},
                    {label: 'Scoreboard', route: '/scoreboard'},
                    {label: 'Training', route: '/training'},
                    {label: 'Blog', route: '/blog'},
                    {label: 'Settings', route: '/settings'},
                    {route: '/logout'}
                ];
                break;
            // admin roles:
            case 'executive':
                headerLinks = [
                    {label: 'Work Orders', route: '/workOrders'},
                    {label: 'Field Customers', route: '/fieldCustomers'},
                    {label: 'HMOD Scoreboard', route: '/hmodScoreboard'},
                    {label: 'Field Kpis', route: '/fieldKpis'},
                    {label: 'Leads', route: '/leads'},
                    {label: 'Customers', route: '/customers'},
                    {label: 'KPIS', route: '/kpis'},
                    {label: 'Scoreboard', route: '/scoreboard'},
                    {label: 'Training', route: '/training'},
                    {label: 'Blog', route: '/blog'},
                    {route: '/logout'}
                ];
                break;
            // field roles (Surveyor, Installer, Inspector, Electrician, Contractor):
            case 'field':
                headerLinks = [
                    {label: 'Scheduled Jobs', route: '/scheduled_jobs'},
                    {label: 'Work Orders', route: '/workOrders'},
                    {label: 'Field Customers', route: '/fieldCustomers'},
                    {label: 'HMOD Scoreboard', route: '/hmodScoreboard'},
                    {label: 'Field Kpis', route: '/fieldKpis'},
                    {route: '/logout'}
                ];
                break;
            // Sales Partners roles
            case 'partner':
                headerLinks = [
                    {label: 'Leads', route: '/leads'},
                    {label: 'Customers', route: '/customers'},
                    {route: '/logout'}
                ];
                break;
            // sales rep and misc roles:
            // n.b. intentional fallthrough:
            case 'rep':
            default:
                headerLinks = [
                    {label: 'Leads', route: '/leads'},
                    {label: 'Customers', route: '/customers'},
                    {label: 'KPIS', route: '/kpis'},
                    {label: 'Scoreboard', route: '/scoreboard'},
                    {label: 'Training', route: '/training'},
                    {label: 'Blog', route: '/blog'},
                    {route: '/logout'}
                ];
                break;
        }

        this.setState({ headerLinks });
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf Header
     *
     * Handler for when mobile "hamburger" icon is clicked -- toggles hiding/showing mobile nav.
     */
    hamburgerIconOnClick = () => {
        this.setState({isMobileNavActive: !this.state.isMobileNavActive});
    };

    render() {
        // since we use the navLinks for both mobile and desktop, separate it out for reuse:
        const navLinks = this.state.headerLinks.map((link) => {
            return (
                <li key={link.route}>
                    {/* clicking on the mobile link triggers closing the mobile nav: */}
                    <Link to={link.route} onClick={() => this.setState({isMobileNavActive: false})}>
                        { link.route === '/logout' ?
                            // if directed to `/logout`, sign out (reset login state) and redirect to `/login`:
                            <FontAwesomeIcon className={css.Header__LogoutIcon} icon={faSignOutAlt} />
                            :
                            link.label
                        }
                    </Link>
                </li>
            );
        });

        return (
            <>
                <header className={`${css.Header} mt-1 mt-md-3 mt-xl-0`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-10 col-md-12 col-xl-4 d-flex align-items-center">
                                <img className={`${css.Header__Logo} mr-3`} src="/assets/logo-V3.png" alt="V3 logo" />

                                {/* TODO: wire to username: */}
                                <span className={`${css.Header__UserName} mx-3`}>{ this.props.user.name }</span>
                            </div>
                            <nav className="col-2 col-md-12 col-md-12 col-xl-8 text-right">
                                {/* ---------- mobile nav: ---------- */}
                                {/* mobile nav button: */}
                                <span className={`${css.Header__HamburgerIcon} d-md-none`} onClick={() => this.hamburgerIconOnClick()}>
                                    <FontAwesomeIcon icon={faBars} />
                                </span>

                                {/* ---------- desktop header links: ---------- */}
                                <ul className={`${css.Header__NavLinks} d-md-inline-flex d-none mt-xl-0 mt-md-3`}>
                                    { navLinks }
                                </ul>
                            </nav>
                        </div>
                    </div>
                </header>

                {/* ---------- mobile nav menu: ---------- */}
                <nav className={this.state.isMobileNavActive ? `${css.Header__MobileNav} ${css.Activate}` : css.Header__MobileNav}>
                    <div className="text-right pt-3 px-4">
                        <FontAwesomeIcon className={css.Header__MobileNavCloseIcon} icon={faTimes} onClick={() => this.hamburgerIconOnClick()} />
                    </div>
                    <ul>
                        { navLinks }
                    </ul>
                </nav>
            </>
        );
    }
};


// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(Header);
